/**
 * Market Sessions API
 * Read-only reference data for market session state and definitions.
 * Backend: GET /api/market-sessions, GET /api/market-sessions/definitions
 */

import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";

/** One session in the current_sessions array */
export interface MarketSessionItem {
  id: string;
  name: string;
  start_utc: string;
  end_utc: string;
}

/** Current state response from GET /market-sessions */
export interface MarketSessionCurrentState {
  instrument?: string;
  timestamp_utc?: string;
  /** Active session(s) at the given time (overlaps possible) */
  current_sessions: MarketSessionItem[];
  /** Whether the market is open (weekday and within at least one session) */
  is_market_open: boolean;
  /** Next week open (e.g. Sunday 22:00 UTC); ISO 8601 */
  next_week_open_utc?: string | null;
  /** Next week close (e.g. Friday 22:00 UTC); ISO 8601 */
  next_week_close_utc?: string | null;
}

/** Session definition for a date (matches backend response) */
export interface MarketSessionDefinition {
  id: string;
  name: string;
  start_utc: string; // ISO 8601
  end_utc: string;   // ISO 8601
}

/** Definitions response from GET /market-sessions/definitions */
export interface MarketSessionDefinitionsResponse {
  instrument?: string;
  date_utc?: string;
  sessions: MarketSessionDefinition[];
}

/**
 * Fetch current market session state for an instrument.
 * @param instrument - e.g. "XAUUSD" or "XAU/USD"
 * @param at - Optional ISO datetime UTC; defaults to now
 */
export async function getMarketSessionCurrentState(
  instrument: string,
  at?: string
): Promise<MarketSessionCurrentState> {
  const params = new URLSearchParams();
  const symbol = instrument.replace("/", "");
  params.set("instrument", symbol);
  if (at) params.set("at", at);
  const url = `/market-sessions?${params.toString()}`;
  const response = await authenticatedFetch(url);
  return parseJsonOrThrow(response, `Market sessions: ${response.status} ${response.statusText}`);
}

/**
 * Fetch session definitions for a date (for timeline).
 * @param instrument - e.g. "XAUUSD" or "XAU/USD"
 * @param date - YYYY-MM-DD
 */
export async function getMarketSessionDefinitions(
  instrument: string,
  date: string
): Promise<MarketSessionDefinitionsResponse> {
  const params = new URLSearchParams();
  const symbol = instrument.replace("/", "");
  params.set("instrument", symbol);
  params.set("date", date);
  const url = `/market-sessions/definitions?${params.toString()}`;
  const response = await authenticatedFetch(url);
  return parseJsonOrThrow(
    response,
    `Market session definitions: ${response.status} ${response.statusText}`
  );
}
